/**
 * SceneCardsBoard — index-card corkboard of the script's scenes.
 *
 * One card per scene (AI one-liner, tone-tinted red/yellow/green), laid out
 * left→right top→bottom in draft order. Drag a card between two others to
 * reorder — nothing touches the script until Commit. The draft persists
 * server-side; Preview opens a read-only pass of the new order.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { CardsPreview } from "@/components/editor/CardsPreview";
import { TensionCurve } from "@/components/editor/TensionCurve";
import { LayoutGrid, ZoomIn, ZoomOut, Maximize2, BookOpen, Check, RotateCcw, Pencil, Loader2, Undo2, Redo2, Activity, Flag, Wand2, RefreshCw } from "lucide-react";

const BASE_W = 230;

const TONES = {
  negative: { bg: "#f3d9d3", border: "#dcab9f", ink: "#7c4437", label: "Negative" },
  positive: { bg: "#dde8d2", border: "#b3c8a0", ink: "#4d6b3a", label: "Positive" },
  neutral: { bg: "#f5ecca", border: "#d9c88f", ink: "#7d6a34", label: "Neutral" },
  unknown: { bg: "#efe9df", border: "#ddd5c7", ink: "#8a8171", label: "" },
};

function rebase(prev, ids) {
  if (!prev) return ids;
  const live = new Set(ids);
  const out = prev.filter((id) => live.has(id));
  const known = new Set(out);
  ids.forEach((id, i) => {
    if (known.has(id)) return;
    let pos = 0;
    for (let j = i - 1; j >= 0; j--) {
      if (known.has(ids[j])) { pos = out.indexOf(ids[j]) + 1; break; }
    }
    out.splice(pos, 0, id);
    known.add(id);
  });
  const same = out.length === prev.length && out.every((v, i) => v === prev[i]);
  return same ? prev : out;
}

export function SceneCardsBoard({ scriptId, elements, readOnly, active, onBeforeCommit, onCommitted }) {
  const confirm = useConfirm();

  const scenes = useMemo(() => {
    const out = [];
    let cur = null, n = 0;
    for (const el of elements || []) {
      if (el.type === "scene_heading") {
        n += 1;
        cur = { sceneId: el.id, number: n, heading: el.text || "(untitled scene)", els: [el] };
        out.push(cur);
      } else if (cur) cur.els.push(el);
    }
    return out;
  }, [elements]);
  const sceneById = useMemo(() => Object.fromEntries(scenes.map((s) => [s.sceneId, s])), [scenes]);
  const scriptIds = useMemo(() => scenes.map((s) => s.sceneId), [scenes]);
  const scriptKey = scriptIds.join("|");

  const [entries, setEntries] = useState({});
  const [pending, setPending] = useState([]);
  const [order, setOrder] = useState(null);
  const [zoom, setZoom] = useState(0.85);
  const [dragId, setDragId] = useState(null);
  const [indicator, setIndicator] = useState(null);
  const [editing, setEditing] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [showCurve, setShowCurve] = useState(() => {
    try { return localStorage.getItem("screenplay_cards_curve") !== "0"; } catch { return true; }
  });
  const [curveH, setCurveH] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("screenplay_cards_curve_h"), 10);
      return Number.isFinite(v) ? Math.min(380, Math.max(96, v)) : 96;
    } catch { return 96; }
  });
  const [markers, setMarkers] = useState([]);
  const [autoActing, setAutoActing] = useState(false);
  const [placingBreak, setPlacingBreak] = useState(false);

  const wrapRef = useRef(null);
  const nodeRefs = useRef(new Map());
  const ghostRef = useRef(null);
  const dragRef = useRef(null);
  const rafRef = useRef(0);
  const lastPt = useRef(null);
  const flipRef = useRef(null);
  const orderRef = useRef(null);
  const indicatorRef = useRef(null);
  const saveTimer = useRef(null);
  const editRef = useRef(null);
  orderRef.current = order;
  indicatorRef.current = indicator;

  const cardW = Math.round(BASE_W * zoom);
  const hdrFs = Math.max(7, Math.min(11, Math.round(cardW * 0.048)));
  const sumFs = Math.max(9, Math.min(14, Math.round(cardW * 0.058)));
  const tiny = cardW < 115;
  const clampLines = Math.max(1, Math.floor((cardW * 0.6 - hdrFs * 2.6 - 10) / (sumFs * 1.35)));

  // -------------------- data --------------------
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/scripts/${scriptId}/cards`);
      const map = {};
      for (const c of data.cards || []) map[c.scene_id] = c;
      setEntries(map);
      setPending(data.pending || []);
      setMarkers(data.markers || []);
      const ids = (data.cards || []).map((c) => c.scene_id);
      setOrder((prev) => rebase(prev || data.draft_order, ids.length ? ids : []));
      setLoaded(true);
    } catch { /* board shows shimmer states */ }
  }, [scriptId]);

  useEffect(() => { if (active) load(); }, [active, load]);

  useEffect(() => {
    if (!active || !pending.length) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [pending, active, load]);

  // rebase local draft when the script's scene set changes
  useEffect(() => {
    setOrder((prev) => (prev ? rebase(prev, scriptIds) : prev));
  }, [scriptKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // persist draft order (debounced)
  useEffect(() => {
    if (readOnly || !loaded || !order) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/scripts/${scriptId}/cards/order`, { order });
      } catch (e) {
        if (e.response?.status === 409) load();
      }
    }, 600);
    return () => clearTimeout(saveTimer.current);
  }, [order]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayOrder = order || scriptIds;
  const moved = useMemo(() => {
    let m = 0;
    displayOrder.forEach((id, i) => { if (scriptIds[i] !== id) m += 1; });
    return m;
  }, [displayOrder, scriptIds]);
  const hasDraft = moved > 0;

  // -------------------- zoom --------------------
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const fn = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => Math.min(1.8, Math.max(0.2, z * (e.deltaY > 0 ? 0.9 : 1.1))));
    };
    el.addEventListener("wheel", fn, { passive: false });
    return () => el.removeEventListener("wheel", fn);
  }, []);

  const fitAll = useCallback(() => {
    const el = wrapRef.current;
    const N = scenes.length;
    if (!el || !N) return;
    const W = el.clientWidth - 24, H = el.clientHeight - 24, gap = 10;
    for (let cols = 1; cols <= N; cols++) {
      const cw = (W - gap * (cols - 1)) / cols;
      const rows = Math.ceil(N / cols);
      if (rows * cw * 0.6 + (rows - 1) * gap <= H) {
        setZoom(Math.min(1.8, Math.max(0.2, cw / BASE_W)));
        return;
      }
    }
    setZoom(0.2);
  }, [scenes.length]);

  // -------------------- drag & drop --------------------
  const computeGap = useCallback((x, y) => {
    const d = dragRef.current;
    const ord = orderRef.current || [];
    if (!d) return;
    let best = -1, bd = Infinity, br = null;
    ord.forEach((sid, i) => {
      if (sid === d.id) return;
      const node = nodeRefs.current.get(sid);
      if (!node) return;
      const r = node.getBoundingClientRect();
      const dist = Math.abs(r.top + r.height / 2 - y) * 2.5 + Math.abs(r.left + r.width / 2 - x);
      if (dist < bd) { bd = dist; best = i; br = r; }
    });
    if (best < 0) return;
    const before = x < br.left + br.width / 2;
    const gap = before ? best : best + 1;
    const nx = before ? br.left - 5 : br.right + 5;
    setIndicator((prev) =>
      prev && prev.gap === gap && Math.abs(prev.y - br.top) < 2 && Math.abs(prev.x - nx) < 2
        ? prev
        : { gap, x: nx, y: br.top, h: br.height });
  }, []);

  const tick = useCallback(() => {
    const d = dragRef.current, pt = lastPt.current;
    if (!d || !d.active || !pt) { rafRef.current = 0; return; }
    if (ghostRef.current) {
      ghostRef.current.style.transform = `translate(${pt.x - d.w / 2}px, ${pt.y - 18}px)`;
    }
    const wrap = wrapRef.current;
    if (wrap) {
      const r = wrap.getBoundingClientRect();
      if (pt.y < r.top + 48) wrap.scrollTop -= 12;
      else if (pt.y > r.bottom - 48) wrap.scrollTop += 12;
    }
    computeGap(pt.x, pt.y);
    rafRef.current = requestAnimationFrame(tick);
  }, [computeGap]);

  const onMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    lastPt.current = { x: e.clientX, y: e.clientY };
    if (!d.active) {
      if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < 6) return;
      d.active = true;
      setDragId(d.id);
    }
    e.preventDefault();
    if (!rafRef.current) rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const onUp = useCallback(() => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (d.active) {
      const ind = indicatorRef.current;
      const ord = orderRef.current || [];
      if (ind) {
        const from = ord.indexOf(d.id);
        let to = ind.gap;
        if (to > from) to -= 1;
        if (to !== from && from >= 0) {
          captureFlip();
          const next = [...ord];
          next.splice(from, 1);
          next.splice(to, 0, d.id);
          setPast((p) => [...p.slice(-49), ord]);
          setFuture([]);
          setOrder(next);
        }
      }
      setDragId(null);
      setIndicator(null);
    } else {
      setEditing(d.id);
    }
  }, [onMove]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCardPointerDown = (e, sid) => {
    if (readOnly || e.button !== 0) return;
    const node = nodeRefs.current.get(sid);
    dragRef.current = { id: sid, x0: e.clientX, y0: e.clientY, active: false, w: node?.offsetWidth || cardW };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // FLIP — animate cards to their new grid slots after a reorder
  const captureFlip = () => {
    const m = new Map();
    nodeRefs.current.forEach((node, id) => { if (node) m.set(id, node.getBoundingClientRect()); });
    flipRef.current = m;
  };
  useLayoutEffect(() => {
    const prev = flipRef.current;
    if (!prev) return;
    flipRef.current = null;
    nodeRefs.current.forEach((node, id) => {
      const p = prev.get(id);
      if (!node || !p) return;
      const n = node.getBoundingClientRect();
      const dx = p.left - n.left, dy = p.top - n.top;
      if (!dx && !dy) return;
      node.style.transition = "none";
      node.style.transform = `translate(${dx}px, ${dy}px)`;
      requestAnimationFrame(() => {
        node.style.transition = "transform 240ms cubic-bezier(.2,.8,.2,1)";
        node.style.transform = "";
      });
    });
  }, [displayOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------- tension curve --------------------
  const toggleCurve = useCallback(() => {
    setPlacingBreak(false);
    setShowCurve((s) => {
      try { localStorage.setItem("screenplay_cards_curve", s ? "0" : "1"); } catch { /* ignore */ }
      return !s;
    });
  }, []);

  const curveItems = useMemo(() => displayOrder.map((sid) => {
    const sc = sceneById[sid];
    if (!sc) return null;
    const e = entries[sid] || {};
    return { sceneId: sid, number: sc.number, heading: sc.heading, tension: e.tension ?? null, sentiment: e.sentiment };
  }).filter(Boolean), [displayOrder, sceneById, entries]);

  const scrollToCard = useCallback((sid) => {
    const node = nodeRefs.current.get(sid);
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    node.style.boxShadow = "0 0 0 3px var(--brand)";
    setTimeout(() => { node.style.boxShadow = ""; }, 1200);
  }, []);

  const handleCurveHeight = useCallback((h) => {
    setCurveH(h);
    try { localStorage.setItem("screenplay_cards_curve_h", String(h)); } catch { /* ignore */ }
  }, []);

  const changeMarkers = useCallback((next) => {
    setMarkers(next);
    api.put(`/scripts/${scriptId}/cards/markers`, { markers: next })
      .catch(() => toast.error("Couldn't save the act breaks."));
  }, [scriptId]);

  const autoPlaceActs = useCallback(async () => {
    if (markers.length) {
      const ok = await confirm({
        title: "Replace your act breaks?",
        description: "Your current act break markers will be swapped for fresh ones placed from the story's shape.",
        confirmLabel: "Replace them",
      });
      if (!ok) return;
    }
    setAutoActing(true);
    try {
      const { data } = await api.post(`/scripts/${scriptId}/cards/auto-acts`);
      setMarkers(data.markers || []);
      toast.success("Act breaks placed — drag any marker to fine-tune.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't place the act breaks.");
    } finally {
      setAutoActing(false);
    }
  }, [scriptId, markers, confirm]);

  // -------------------- undo / redo (card moves) --------------------
  const undo = useCallback(() => {
    if (!past.length) return;
    const prev = past[past.length - 1];
    captureFlip();
    setFuture((f) => [...f, orderRef.current]);
    setOrder(rebase(prev, scriptIds));
    setPast(past.slice(0, -1));
  }, [past, scriptIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const redo = useCallback(() => {
    if (!future.length) return;
    const next = future[future.length - 1];
    captureFlip();
    setPast((p) => [...p.slice(-49), orderRef.current]);
    setOrder(rebase(next, scriptIds));
    setFuture(future.slice(0, -1));
  }, [future, scriptIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  undoRef.current = undo;
  redoRef.current = redo;

  useEffect(() => {
    if (!active || readOnly) return;
    const fn = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k !== "z" && k !== "y") return;
      const t = e.target;
      if (t && (t.tagName === "TEXTAREA" || t.tagName === "INPUT" || t.isContentEditable)) return;
      e.preventDefault();
      if (k === "y" || (k === "z" && e.shiftKey)) redoRef.current();
      else undoRef.current();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [active, readOnly]);

  // -------------------- actions --------------------
  const doCommit = useCallback(async () => {
    const ok = await confirm({
      title: "Commit this scene order?",
      description: "The script itself will be rewritten in the new order. This is the moment it becomes real.",
      confirmLabel: "Commit order",
    });
    if (!ok) return;
    setCommitting(true);
    try {
      await onBeforeCommit?.();
      await api.put(`/scripts/${scriptId}/cards/order`, { order: orderRef.current });
      const { data } = await api.post(`/scripts/${scriptId}/cards/commit`);
      onCommitted?.(data.elements, data.version);
      setPreviewOpen(false);
      setPast([]);
      setFuture([]);
      toast.success("Scene order committed to the script");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't commit the order.");
    } finally {
      setCommitting(false);
    }
  }, [confirm, scriptId, onBeforeCommit, onCommitted]);

  const doDiscard = useCallback(async () => {
    const ok = await confirm({
      title: "Discard the draft order?",
      description: "Cards return to the script's current order. The script itself was never changed.",
      confirmLabel: "Discard draft",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.post(`/scripts/${scriptId}/cards/discard`);
      captureFlip();
      setPast((p) => [...p.slice(-49), orderRef.current]);
      setFuture([]);
      setOrder(scriptIds);
    } catch {
      toast.error("Couldn't discard the draft.");
    }
  }, [confirm, scriptId, scriptIds]);

  const saveSummary = async () => {
    const text = (editRef.current?.value || "").trim();
    if (!text || !editing) return;
    try {
      const { data } = await api.put(`/scripts/${scriptId}/cards/${editing}`, { summary: text });
      setEntries((prev) => ({ ...prev, [editing]: { ...prev[editing], ...data.card } }));
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the summary.");
    }
  };

  // -------------------- re-summarize --------------------
  const [resummarizing, setResummarizing] = useState(false);
  const [redoing, setRedoing] = useState(() => new Set());

  const resummarizeAll = useCallback(async () => {
    setResummarizing(true);
    try {
      const { data } = await api.post(`/scripts/${scriptId}/cards/resummarize`);
      const pend = data.pending || [];
      if (!pend.length) {
        toast("Every card is a hand-edit of yours — use the redo arrow on a card to replace one.");
        return;
      }
      setPending(pend);
      toast.success(`Re-reading ${pend.length} scene${pend.length > 1 ? "s" : ""} — fresh cards in a moment.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the re-summarize.");
    } finally {
      setResummarizing(false);
    }
  }, [scriptId]);

  const redoCard = useCallback(async (sid) => {
    setRedoing((p) => new Set(p).add(sid));
    try {
      const { data } = await api.post(`/scripts/${scriptId}/cards/${sid}/resummarize`);
      setEntries((prev) => ({ ...prev, [sid]: { ...prev[sid], ...data.card } }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't re-summarize that card.");
    } finally {
      setRedoing((p) => { const n = new Set(p); n.delete(sid); return n; });
    }
  }, [scriptId]);

  // -------------------- render --------------------
  const renderCardInner = (sc, e, i) => {
    const tone = TONES[e.sentiment] || TONES.unknown;
    return (
      <>
        <div className="flex items-center gap-1 px-2 pt-1.5 font-sans" style={{ fontSize: hdrFs, color: tone.ink }}>
          <span className="font-bold shrink-0">Sc {sc.number}</span>
          {i != null && i + 1 !== sc.number && <span className="shrink-0 font-bold text-[var(--brand)]">→ {i + 1}</span>}
          {!tiny && <span className="uppercase tracking-wide truncate opacity-75">{sc.heading}</span>}
          {!tiny && (e.custom || e.pending || redoing.has(sc.scene_id)) && (
            <span className="ml-auto flex items-center gap-1 shrink-0">
              {e.custom && <Pencil size={Math.max(8, hdrFs)} className="opacity-50" />}
              {(e.pending || redoing.has(sc.scene_id)) && e.summary && <Loader2 size={Math.max(8, hdrFs)} className="animate-spin opacity-60" />}
            </span>
          )}
        </div>
        <div style={{ borderTop: `1px solid ${tone.border}`, margin: "3px 8px 0" }} />
        {!tiny && (
          <div className="px-2 pt-1 font-sans" style={{
            fontSize: sumFs, lineHeight: 1.35, color: "#40392f",
            display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: clampLines, overflow: "hidden",
          }}>
            {e.summary
              ? e.summary
              : <span className="inline-flex items-center gap-1 opacity-60"><Loader2 size={sumFs} className="animate-spin" /> Summarizing…</span>}
          </div>
        )}
      </>
    );
  };

  const renderCard = (sid, i) => {
    const sc = sceneById[sid];
    if (!sc) return null;
    const e = entries[sid] || {};
    const tone = TONES[e.sentiment] || TONES.unknown;
    return (
      <div
        key={sid}
        ref={(node) => { if (node) nodeRefs.current.set(sid, node); else nodeRefs.current.delete(sid); }}
        onPointerDown={(ev) => onCardPointerDown(ev, sid)}
        className={`group relative rounded-md border shadow-sm overflow-hidden select-none ${readOnly ? "" : "cursor-grab"} ${dragId === sid ? "opacity-30" : ""}`}
        style={{ background: tone.bg, borderColor: tone.border, aspectRatio: "5 / 3", touchAction: readOnly ? "auto" : "none" }}
        title={readOnly ? sc.heading : `${sc.heading}\nDrag to reorder · click to edit the summary`}
        data-testid={`scene-card-${sc.number}`}
      >
        {renderCardInner(sc, e, i)}
        {!readOnly && !tiny && (
          <button
            onPointerDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => { ev.stopPropagation(); redoCard(sid); }}
            disabled={redoing.has(sid)}
            className="absolute bottom-1 right-1 z-10 p-1 rounded bg-white/75 text-[var(--ink-muted)] opacity-0 group-hover:opacity-100 hover:text-[var(--brand)] transition-opacity disabled:opacity-60"
            title={e.custom ? "Replace your edit with a fresh summary of the scene's current text" : "Re-summarize this card from the scene's current text"}
            data-testid={`card-redo-${sc.number}`}
          >
            <RefreshCw size={11} className={redoing.has(sid) ? "animate-spin" : ""} />
          </button>
        )}
      </div>
    );
  };

  const draggedScene = dragId ? sceneById[dragId] : null;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="cards-board">
      {/* toolbar */}
      <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 px-3 py-2 border-b border-[var(--line)] bg-[var(--paper)] shrink-0" data-testid="cards-toolbar">
        <span className="flex items-center gap-2 font-display text-base text-[var(--ink-primary)]">
          <LayoutGrid size={15} className="text-[var(--brand)]" /> Scene Cards
        </span>
        <span className="text-[11px] text-[var(--ink-muted)]" data-testid="cards-count">{scenes.length} scenes</span>
        <span className="hidden md:flex items-center gap-2 text-[10px] text-[var(--ink-muted)]">
          {["negative", "neutral", "positive"].map((k) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm border" style={{ background: TONES[k].bg, borderColor: TONES[k].border }} />
              {TONES[k].label}
            </span>
          ))}
        </span>
        <button
          onClick={toggleCurve}
          className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border transition-colors ${showCurve ? "border-[var(--brand)] text-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"}`}
          title="Tension curve — your story's shape, scene by scene"
          data-testid="cards-curve-toggle"
        >
          <Activity size={13} /> Curve
        </button>
        {!readOnly && scenes.length > 0 && (
          <button
            onClick={resummarizeAll}
            disabled={resummarizing || pending.length > 0}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[var(--line)] text-[var(--ink-secondary)] hover:text-[var(--brand)] hover:border-[var(--brand)]/50 transition-colors disabled:opacity-60"
            title="Re-read every scene and rewrite its card from the current text (your hand-edited cards are kept)"
            data-testid="cards-resummarize-btn"
          >
            <RefreshCw size={13} className={resummarizing || pending.length > 0 ? "animate-spin" : ""} />
            {pending.length > 0 ? `Summarizing ${pending.length}…` : "Re-summarize"}
          </button>
        )}
        {showCurve && !readOnly && scenes.length > 1 && (
          <>
            {scenes.length >= 4 && (
              <button
                onClick={autoPlaceActs}
                disabled={autoActing}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-[var(--line)] text-[var(--ink-secondary)] hover:text-[var(--brand)] hover:border-[var(--brand)]/50 transition-colors disabled:opacity-60"
                title="Read the story's shape and place Act II, Midpoint and Act III for you"
                data-testid="curve-auto-acts"
              >
                {autoActing ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                {autoActing ? "Reading your story…" : "Place acts for me"}
              </button>
            )}
            <button
              onClick={() => setPlacingBreak((p) => !p)}
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border transition-colors ${placingBreak ? "border-[var(--brand)] text-white bg-[var(--brand)]" : "border-[var(--line)] text-[var(--ink-secondary)] hover:text-[var(--brand)] hover:border-[var(--brand)]/50"}`}
              title="Drop an act break line on the curve"
              data-testid="curve-add-marker"
            >
              <Flag size={13} /> {placingBreak ? "Click between two scenes — Esc to cancel" : "Act break"}
            </button>
          </>
        )}

        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {hasDraft ? (
            <>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#f0e0b6] text-[#7a5d1d] border border-[#dcc27a] font-sans" data-testid="cards-draft-badge">
                Draft — {moved} position{moved > 1 ? "s" : ""} changed
              </span>
              <button onClick={() => setPreviewOpen(true)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-[var(--line)] hover:border-[var(--brand)]/60 hover:bg-[var(--warm-accent)] transition-colors" data-testid="cards-preview-btn">
                <BookOpen size={13} /> Preview
              </button>
              {!readOnly && (
                <>
                  <button onClick={doCommit} disabled={committing} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md btn-brand disabled:opacity-50" data-testid="cards-commit-btn">
                    {committing ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Commit
                  </button>
                  <button onClick={doDiscard} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-[var(--line)] text-[var(--ink-secondary)] hover:text-[var(--err)] hover:border-[var(--err)]/50 transition-colors" data-testid="cards-discard-btn">
                    <RotateCcw size={13} /> Discard
                  </button>
                </>
              )}
            </>
          ) : (
            loaded && scenes.length > 0 && <span className="text-[11px] text-[var(--ink-muted)] font-sans" data-testid="cards-in-sync">Matches script order</span>
          )}
          {!readOnly && (
            <div className="flex items-center rounded-md border border-[var(--line)] overflow-hidden ml-1">
              <button onClick={undo} disabled={!past.length} className="p-1.5 hover:bg-[var(--warm-accent)] disabled:opacity-30" title="Undo card move (⌘Z)" data-testid="cards-undo-btn"><Undo2 size={13} /></button>
              <button onClick={redo} disabled={!future.length} className="p-1.5 border-l border-[var(--line)] hover:bg-[var(--warm-accent)] disabled:opacity-30" title="Redo card move (⌘⇧Z)" data-testid="cards-redo-btn"><Redo2 size={13} /></button>
            </div>
          )}
          <div className="flex items-center rounded-md border border-[var(--line)] overflow-hidden ml-1">
            <button onClick={() => setZoom((z) => Math.max(0.2, z * 0.85))} className="p-1.5 hover:bg-[var(--warm-accent)]" title="Zoom out" data-testid="cards-zoom-out"><ZoomOut size={13} /></button>
            <span className="text-[10px] text-[var(--ink-muted)] px-1 w-9 text-center font-mono-script" data-testid="cards-zoom-level">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((z) => Math.min(1.8, z * 1.18))} className="p-1.5 hover:bg-[var(--warm-accent)]" title="Zoom in" data-testid="cards-zoom-in"><ZoomIn size={13} /></button>
            <button onClick={fitAll} className="p-1.5 border-l border-[var(--line)] hover:bg-[var(--warm-accent)]" title="Fit the whole script on screen" data-testid="cards-zoom-fit"><Maximize2 size={13} /></button>
          </div>
        </div>
      </div>

      {/* tension curve — the story's shape in the current card order */}
      {showCurve && scenes.length > 0 && (
        <TensionCurve
          items={curveItems}
          onDotClick={scrollToCard}
          height={curveH}
          onHeightChange={handleCurveHeight}
          markers={markers}
          onMarkersChange={readOnly ? undefined : changeMarkers}
          placing={placingBreak}
          onPlacingChange={setPlacingBreak}
          readOnly={readOnly}
        />
      )}

      {/* grid */}
      <div ref={wrapRef} className="flex-1 overflow-y-auto relative bg-[var(--canvas)]">
        {scenes.length === 0 ? (
          <p className="font-sans text-sm text-[var(--ink-muted)] italic p-6" data-testid="cards-empty">
            No scenes yet — add scene headings in the Script tab and they'll show up here as cards.
          </p>
        ) : (
          <div className="p-3 grid" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${cardW}px, 1fr))`, gap: 10 }} data-testid="cards-grid">
            {displayOrder.map((sid, i) => renderCard(sid, i))}
          </div>
        )}
      </div>

      {/* drop indicator */}
      {indicator && dragId && (
        <div className="fixed z-[70] rounded-full bg-[var(--brand)] pointer-events-none" style={{ left: indicator.x - 2, top: indicator.y, width: 4, height: indicator.h, boxShadow: "0 0 0 3px rgba(211,110,90,0.25)" }} data-testid="cards-drop-indicator" />
      )}

      {/* drag ghost */}
      {draggedScene && (
        <div ref={ghostRef} className="fixed left-0 top-0 z-[71] pointer-events-none rounded-md border shadow-xl overflow-hidden rotate-2" style={{
          width: dragRef.current?.w || cardW, aspectRatio: "5 / 3",
          background: (TONES[(entries[dragId] || {}).sentiment] || TONES.unknown).bg,
          borderColor: (TONES[(entries[dragId] || {}).sentiment] || TONES.unknown).border,
        }} data-testid="cards-drag-ghost">
          {renderCardInner(draggedScene, entries[dragId] || {}, null)}
        </div>
      )}

      {/* summary editor */}
      {editing && sceneById[editing] && createPortal(
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" data-testid="card-edit-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditing(null)} />
          <div className="relative bg-[var(--paper)] border border-[var(--line)] rounded-xl shadow-2xl w-full max-w-md p-4">
            <p className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] font-sans mb-1">Sc {sceneById[editing].number} — {sceneById[editing].heading}</p>
            <textarea
              key={editing}
              ref={editRef}
              defaultValue={(entries[editing] || {}).summary || ""}
              rows={3}
              maxLength={300}
              className="w-full bg-[var(--canvas)] border border-[var(--line)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--brand)] resize-none font-sans"
              placeholder="One sentence — what happens in this scene?"
              data-testid="card-edit-input"
            />
            <p className="text-[10px] text-[var(--ink-muted)] font-sans mt-1">Your wording sticks until the scene's text changes — then the card re-summarizes.</p>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded-md border border-[var(--line)] hover:bg-[var(--warm-accent)]" data-testid="card-edit-cancel">Cancel</button>
              <button onClick={saveSummary} className="text-xs px-3 py-1.5 rounded-md btn-brand" data-testid="card-edit-save">Save summary</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* read-only preview of the draft order */}
      {previewOpen && (
        <CardsPreview
          scenes={scenes}
          order={displayOrder}
          moved={moved}
          readOnly={readOnly}
          committing={committing}
          onCommit={doCommit}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </div>
  );
}
